// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/coret/ReactiveProfileSettings.module.css';
import {fileToDataUrl, getReactiveProfile, updateReactiveProfile} from '@app/features/coret/CoretMediaApi';
import {copyCurrentCallOverlayLink} from '@app/features/coret/CoretOverlayActions';
import {useEffect, useRef, useState} from 'react';

type Slot = 'idle' | 'speaking';

export function ReactiveProfileSettings() {
	const [idle, setIdle] = useState<string | null>(null);
	const [speaking, setSpeaking] = useState<string | null>(null);
	const [pending, setPending] = useState<Partial<Record<Slot, string | null>>>({});
	const [busy, setBusy] = useState(false);
	const [message, setMessage] = useState('');
	const [error, setError] = useState(false);
	const idleRef = useRef<HTMLInputElement>(null);
	const speakingRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		void getReactiveProfile()
			.then((profile) => {
				setIdle(profile.idle_image_url);
				setSpeaking(profile.speaking_image_url);
			})
			.catch(() => {
				setError(true);
				setMessage('Não foi possível carregar o Reactive agora.');
			});
	}, []);
	const pick = async (slot: Slot, file?: File) => {
		if (!file) return;
		if (file.size > 5 * 1024 * 1024) {
			setError(true);
			setMessage('Use uma imagem de até 5 MB.');
			return;
		}
		const data = await fileToDataUrl(file);
		setPending((current) => ({...current, [slot]: data}));
		setError(false);
		setMessage('Imagem pronta. Clique em Salvar Reactive.');
	};
	const save = async () => {
		setBusy(true);
		setMessage('Salvando...');
		setError(false);
		try {
			const profile = await updateReactiveProfile({
				...(Object.hasOwn(pending, 'idle') ? {idle_image_data: pending.idle} : {}),
				...(Object.hasOwn(pending, 'speaking') ? {speaking_image_data: pending.speaking} : {}),
			});
			setIdle(profile.idle_image_url);
			setSpeaking(profile.speaking_image_url);
			setPending({});
			setMessage('Reactive salvo. Ele já será usado nos overlays das calls.');
		} catch (saveError) {
			setError(true);
			setMessage(saveError instanceof Error ? saveError.message : 'Não foi possível salvar.');
		} finally {
			setBusy(false);
		}
	};
	const preview = (slot: Slot) => (Object.hasOwn(pending, slot) ? pending[slot] : slot === 'idle' ? idle : speaking);
	return (
		<section className={styles.card} aria-labelledby="coret-reactive-heading">
			<h3 id="coret-reactive-heading" className={styles.heading}>
				Reactive
			</h3>
			<p className={styles.description}>
				Escolha uma imagem para quando você estiver em silêncio e outra para quando estiver falando. O overlay da call
				alterna entre elas no OBS. Seu link é permanente e acompanha qualquer call em que você entrar.
			</p>
			<div className={styles.grid}>
				{(['idle', 'speaking'] as const).map((slot) => (
					<div className={styles.slot} key={slot}>
						<span className={styles.label}>{slot === 'idle' ? 'Em silêncio' : 'Falando'}</span>
						<div className={styles.preview}>
							{preview(slot) ? (
								<img src={preview(slot) ?? ''} alt="" />
							) : (
								<span className={styles.empty}>Nenhuma imagem</span>
							)}
						</div>
						<div className={styles.actions}>
							<button
								type="button"
								className={styles.button}
								onClick={() => (slot === 'idle' ? idleRef : speakingRef).current?.click()}
							>
								Escolher imagem
							</button>
							{preview(slot) && (
								<button
									type="button"
									className={`${styles.button} ${styles.buttonSecondary}`}
									onClick={() => setPending((current) => ({...current, [slot]: null}))}
								>
									Remover
								</button>
							)}
						</div>
					</div>
				))}
			</div>
			<input
				ref={idleRef}
				className={styles.input}
				type="file"
				accept="image/png,image/jpeg,image/webp,image/gif"
				onChange={(event) => void pick('idle', event.target.files?.[0])}
			/>
			<input
				ref={speakingRef}
				className={styles.input}
				type="file"
				accept="image/png,image/jpeg,image/webp,image/gif"
				onChange={(event) => void pick('speaking', event.target.files?.[0])}
			/>
			<div className={styles.actions}>
				<button
					type="button"
					className={styles.button}
					disabled={busy || Object.keys(pending).length === 0}
					onClick={() => void save()}
				>
					Salvar Reactive
				</button>
				<button
					type="button"
					className={`${styles.button} ${styles.buttonSecondary}`}
					onClick={() => void copyCurrentCallOverlayLink()}
				>
					Copiar meu link permanente do OBS
				</button>
			</div>
			<div className={`${styles.status} ${error ? styles.error : ''}`} role="status">
				{message}
			</div>
		</section>
	);
}
